import useManuscriptReportChoice from "../../../hooks/useManuscriptReportChoice";
import ManuscriptReportChoice from "../../ManuscriptReportChoice";
import { getBrowserTimeZone } from "../../../utils/getBrowserTimeZone";
import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Auth from "../../../Auth";
import GeneralBaseModal from "../GeneralBaseModal";
import Dropzone from "react-dropzone";
import classNames from "classnames";
import InlineSVG from "react-inlinesvg";
import { Container } from "./styles";
import { MAX_TRIAL_NOVEL_IMPORTS, MAX_IMPORT_WIP_INPUT_WORDS, MAX_AGENTIC_IMPORT_INPUT_WORDS, MAX_SCRIVENER_ZIP_BYTES } from "../../../../../src/shared/constants";
import { determineScrivenerToastMessage } from "./scrivenerImportErrors";
import { useAllFoldersStore } from "../../AppHome/store";
import { useToast } from "../../ToastWrapper";
import { sendAppEvent } from "../../../Utils";
import { SudoSegmentedControl } from "src/sudokit/components/SudoSegmentedControl";
import useImportSpeedPerceptionExperiment, {
  markImportSpeedPerceptionSession,
  persistImportSpeedPerceptionCohortIfNeeded,
} from "../../../hooks/useImportSpeedPerceptionExperiment";
import useUserMetadata from "../../../hooks/useUserMetadata";
import useNovelImportAccess from "../../../hooks/useNovelImportAccess";
import { IMPORT_SPEED_PERCEPTION_COHORT_KEY } from "../../../../../src/shared/constants";
import { waitForNovelImportReportGate } from "./waitForNovelImportReportGate";
import ImportFailedDialog from "./ImportFailedDialog";
import { isHomePath } from "../../../utils/isHomePath";

const TAB_OPTIONS = [
  { value: "document", label: "Import Document" },
  { value: "scrivener", label: "Import Scrivener" },
];

const LEGACY_TAB_OPTION = { value: "legacy", label: "Legacy Import" };

export default function ImportNovel({ user, isOpen, setIsOpen, userCanImport, folderParentId, hasAccessToAgenticNovelImport = false, initialTab = "document" }) {
  const [selectedTab, setActiveTab] = useState(initialTab);
  const importAccess = useNovelImportAccess({ userId: user?.uid, hasAccessToAgenticNovelImport });
  const activeTab = selectedTab === "legacy" && importAccess.hasAgenticAccess ? "document" : selectedTab;
  const documentImportType = activeTab === "legacy" ? "Classic" : "Agentic";
  const isDocumentTab = activeTab !== "scrivener";
  const tabOptions = importAccess.hasAgenticAccess ? TAB_OPTIONS : [...TAB_OPTIONS, LEGACY_TAB_OPTION];
  const [loading, setLoading] = useState(false);
  const reportChoice = useManuscriptReportChoice();
  const [importFailureDialog, setImportFailureDialog] = useState(null);
  const { setToast } = useToast();
  const { importProject, archiveDeleteProject, update } = useAllFoldersStore();
  const history = useHistory();
  const importSpeedPerception = useImportSpeedPerceptionExperiment();
  const { userMetadata, refetch: refetchUserMetadata } = useUserMetadata();
  const persistedCohort = userMetadata?.experiments?.[IMPORT_SPEED_PERCEPTION_COHORT_KEY] ?? null;

  const markTreatmentImportSession = async (projectId) => {
    if (!importSpeedPerception) return;
    const persisted = await persistImportSpeedPerceptionCohortIfNeeded(importSpeedPerception, persistedCohort);
    if (persisted) {
      await refetchUserMetadata();
    }
    markImportSpeedPerceptionSession(projectId);
  };

  useEffect(() => {
    if (isOpen) {
      sendAppEvent(isDocumentTab ? "import_novel_modal_viewed" : "import_scrivener_modal_viewed");
    }
  }, [isOpen, activeTab, isDocumentTab]);

  // Reset tab to initial when modal opens
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab, hasAccessToAgenticNovelImport]);

  const reset = () => {
    setIsOpen(false);
    setLoading(false);
  };

  const maxImportWords = documentImportType === "Agentic" ? MAX_AGENTIC_IMPORT_INPUT_WORDS : MAX_IMPORT_WIP_INPUT_WORDS;

  // Document import error messages
  const determineDocumentToastMessage = (error) => {
    switch (error) {
      case "Max words exceeded":
        return `Could not import your project because it exceeds the maximum word count. Please upload a file with ${maxImportWords.toLocaleString()} words or fewer.`;
      case "Max imports for trial user exceeded.":
        return (
          <>
            You can only import {MAX_TRIAL_NOVEL_IMPORTS} novels while on your trial.
            <a
              target="_blank"
              href="https://editor.sudowrite.com/app/plans?ref=import-modal"
            >
              <strong>Subscribe</strong>
            </a>
          </>
        );
      case "Too many requests":
        return "You can only import one novel at a time. Please wait for the current import to complete before starting a new one.";
      default:
        return "Could not import your project. Please try again.";
    }
  };


  const handleImport = async (documentId, projectId, originalFilename) => {
    if (!documentId || !projectId) {
      throw new Error("Missing required IDs for import");
    }

    const importPayload = {
      projectId,
      documentId,
      originalFilename,
      timeZone: getBrowserTimeZone(),
      enableImportReport: reportChoice.enableImportReport,
    };

    importPayload.importType = documentImportType;

    const response = await Auth.fetchWithIdToken("/api/ai/storyEngine/importNovel", importPayload);
    return response;
  };

  // Document import handler
  const importDocument = async (file) => {
    if (!reportChoice.ready) return;
    if (!userCanImport) {
      reset();
      setToast(
        <>
          <InlineSVG
            src="/assets/close.svg"
            className="failed"
          />
          {determineDocumentToastMessage("Max imports for trial user exceeded.")}
        </>
      );
      return;
    }

    setIsOpen(false);
    let createdProjectId = null;
    try {
      const title = file.name.replace(/\.[^/.]+$/, ""); // remove file extension
      const project = await importProject(title, folderParentId);
      createdProjectId = project.id;
      await markTreatmentImportSession(project.id);
      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", project.id);
      const result = await Auth.postFilesWithIdToken("/api/import", { user, projectId: project.id }, formData);
      if (!result?.document) {
        throw new Error("Invalid response from import service");
      }
      const { document } = result;
      // Mark this tab as the one that initiated the import so only this tab auto-navigates
      sessionStorage.setItem("pendingImportProjectId", project.id);
      const response = await handleImport(document.id, project.id, file.name);
      if (importSpeedPerception && response.enableImportReport) {
        await waitForNovelImportReportGate(project.id);
        const currentPath = history.location.pathname;
        if (isHomePath(currentPath) || currentPath === `/app/project/${project.id}`) {
          sendAppEvent("import_novel_report_ready_navigated", { projectId: project.id });
          history.push(`/app/project/${project.id}`);
        }
      }
      reset();
    } catch (err) {
      if (createdProjectId) {
        sessionStorage.removeItem("pendingImportProjectId");
      }
      if (importSpeedPerception && err.message === "import_failed") {
        await update();
        setImportFailureDialog({
          projectId: err.projectId || createdProjectId,
          hasImportReport: !!err.hasImportReport,
        });
        reset();
        return;
      }
      const error = err.response ? (await err.response.json()).error : err.message;
      sendAppEvent("import_novel_failed", { error, importType: "document" });
      setToast(
        <>
          <InlineSVG
            src="/assets/close.svg"
            className="failed"
          />
          {determineDocumentToastMessage(error)}
        </>
      );
      reset();
      throw err;
    }
  };

  // Scrivener import handler
  const importScrivenerFile = async (file) => {
    if (!userCanImport) {
      reset();
      setToast(
        <>
          <InlineSVG
            src="/assets/close.svg"
            className="failed"
          />
          {"You cannot import at this time. Please try again later."}
        </>
      );
      return;
    }

    setIsOpen(false);
    let project;
    try {
      const title = file.name.replace(/\.zip$/i, "").replace(/-\d{4}-\d{2}-\d{2}$/, ""); // Remove .zip and date suffix
      project = await importProject(title, folderParentId);
      await markTreatmentImportSession(project.id);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("projectId", project.id);

      // Mark this tab as the one that initiated the import so only this tab auto-navigates
      sessionStorage.setItem("pendingImportProjectId", project.id);
      sendAppEvent("import_scrivener_started", { projectId: project.id });
      await Auth.postFilesWithIdToken("/api/import-scrivener", { user, projectId: project.id }, formData);

      if (importSpeedPerception) {
        const currentPath = history.location.pathname;
        if (isHomePath(currentPath) || currentPath === `/app/project/${project.id}`) {
          history.push(`/app/project/${project.id}`);
        }
      }

      reset();
    } catch (err) {
      let error;
      try {
        error = err.response ? (await err.response.json()).error : err.message;
      } catch {
        error = err.message || "Unknown error";
      }
      sendAppEvent("import_scrivener_failed", { error, importType: "scrivener" });

      // Delete the project that was created before the upload attempt
      if (project) {
        await archiveDeleteProject(project);
      }

      setToast(
        <>
          <InlineSVG
            src="/assets/close.svg"
            className="failed"
          />
          {determineScrivenerToastMessage(error)}
        </>
      );
      reset();
      throw err;
    }
  };

  const title = { main: "Import your Novel" };

  const subtitle =
    isDocumentTab
      ? "Bring a work in progress or completed novel into Sudowrite. We'll split it into chapters and create a detailed Story Bible for you. Free while in beta."
      : "Bring your Scrivener project into Sudowrite. We'll preserve your chapter structure and create documents for all of your text.";

  const handleTabChange = (value) => {
    setActiveTab(value);
    sendAppEvent({
      document: "import_novel_tab_document_clicked",
      scrivener: "import_novel_tab_scrivener_clicked",
      legacy: "import_novel_tab_legacy_clicked",
    }[value]);
  };

  return (
    <>
    <GeneralBaseModal
      title={title}
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      isBeta={true}
      onClose={() => sendAppEvent(isDocumentTab ? "import_novel_modal_dismissed" : "import_scrivener_modal_dismissed")}
    >
      <Container>
        {importAccess.isLoading ? <p role="status">Loading import options...</p> : importAccess.error ? (
          <p role="alert">Could not load import options. <button type="button" onClick={() => importAccess.retry()}>Try again</button></p>
        ) : <>
        <div className="tabs-container">
          <SudoSegmentedControl
            options={tabOptions}
            value={activeTab}
            onChange={handleTabChange}
            aria-label="Import type selection"
          />
        </div>
        <p className="subtitle">{subtitle}</p>

        {isDocumentTab && (
          <>
            <ManuscriptReportChoice choice={reportChoice} disabled={loading} />
            <Dropzone
              disabled={!reportChoice.ready || loading}
              aria-label="import document"
              maxFiles={1}
              multiple={false}
              accept={{
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx", ".doc"],
                "text/plain": [".txt"],
                "application/rtf": [".rtf"],
                "application/vnd.oasis.opendocument.text": [".odt"],
              }}
              onDrop={async (acceptedFiles, rejectedFiles) => {
                sendAppEvent("import_novel_modal_file_dropped");
                if (rejectedFiles.length > 0) {
                  sendAppEvent("import_novel_modal_file_rejected");
                  const hasTooMany = rejectedFiles.some((f) =>
                    f.errors?.some((e) => e.code === "too-many-files")
                  );
                  setToast(
                    <>
                      <InlineSVG
                        src="/assets/close.svg"
                        className="failed"
                      />
                      {hasTooMany
                        ? "Please upload one file at a time."
                        : "Could not upload your file. Please upload a .txt, .doc, .docx, .rtf, or .odt file."}
                    </>
                  );
                  return;
                }
                setLoading(true);
                try {
                  await importDocument(acceptedFiles[0]);
                } catch (error) {
                  setLoading(false);
                }
              }}
            >
              {({ getRootProps, getInputProps, isDragReject, isDragAccept }) => (
                <div
                  {...getRootProps({ onClick: () => sendAppEvent("import_novel_modal_click_to_browse") })}
                  className={classNames({ upload: true, rejected: isDragReject, accepted: isDragAccept })}
                >
                  <input {...getInputProps()} />
                  {!loading && (
                    <>
                      <InlineSVG
                        src="/assets/import.svg"
                        className="import-icon"
                      />
                      <p className="drag-title">
                        Drag your file here or <a href="#">browse files</a>
                      </p>
                      <p className="supported-format">TEXT, DOC, DOCX, RTF, ODT</p>
                    </>
                  )}
                  {loading && <p>Uploading your file...</p>}
                </div>
              )}
            </Dropzone>
          </>
        )}

        {activeTab === "scrivener" && (
          <>
            <div className="instructions">
              <h4>How to create a Scrivener backup</h4>
              <ol>
                <li>
                  In Scrivener, go to <strong>File → Back Up → Back Up To...</strong>
                </li>
                <li>
                  Check <strong>"Back up as ZIP file"</strong>
                </li>
                <li>Save the backup to your computer</li>
                <li>Upload the ZIP file below</li>
              </ol>
            </div>

            <Dropzone
              aria-label="import scrivener"
              maxFiles={1}
              multiple={false}
              maxSize={MAX_SCRIVENER_ZIP_BYTES}
              accept={{
                "application/zip": [".zip"],
                "application/x-zip-compressed": [".zip"],
              }}
              onDrop={async (acceptedFiles, rejectedFiles) => {
                sendAppEvent("import_scrivener_modal_file_dropped");
                if (rejectedFiles.length > 0) {
                  sendAppEvent("import_scrivener_modal_file_rejected");
                  const hasTooMany = rejectedFiles.some((f) =>
                    f.errors?.some((e) => e.code === "too-many-files")
                  );
                  const hasTooLarge = rejectedFiles.some((f) =>
                    f.errors?.some((e) => e.code === "file-too-large")
                  );
                  let message;
                  if (hasTooLarge) {
                    message = determineScrivenerToastMessage("Scrivener ZIP too large");
                  } else if (hasTooMany) {
                    message = "Please upload one file at a time.";
                  } else {
                    message = "Please upload a .zip file from Scrivener's backup feature.";
                  }
                  setToast(
                    <>
                      <InlineSVG
                        src="/assets/close.svg"
                        className="failed"
                      />
                      {message}
                    </>
                  );
                  return;
                }
                setLoading(true);
                try {
                  await importScrivenerFile(acceptedFiles[0]);
                } catch (error) {
                  setLoading(false);
                }
              }}
            >
              {({ getRootProps, getInputProps, isDragReject, isDragAccept }) => (
                <div
                  {...getRootProps({ onClick: () => sendAppEvent("import_scrivener_modal_click_to_browse") })}
                  className={classNames({ upload: true, rejected: isDragReject, accepted: isDragAccept })}
                >
                  <input {...getInputProps()} />
                  {!loading && (
                    <>
                      <InlineSVG
                        src="/assets/import.svg"
                        className="import-icon"
                      />
                      <p className="drag-title">
                        Drag your Scrivener backup here or <a href="#">browse files</a>
                      </p>
                      <p className="supported-format">SCRIVENER BACKUP ZIP</p>
                    </>
                  )}
                  {loading && <p>Uploading your Scrivener project...</p>}
                </div>
              )}
            </Dropzone>
          </>
        )}
        </>}
      </Container>
    </GeneralBaseModal>
    <ImportFailedDialog
      isOpen={!!importFailureDialog}
      hasImportReport={!!importFailureDialog?.hasImportReport}
      onClose={() => setImportFailureDialog(null)}
      onOpenProject={() => {
        if (importFailureDialog?.projectId) {
          history.push(`/app/project/${importFailureDialog.projectId}`);
        }
        setImportFailureDialog(null);
      }}
      onTryAgain={() => {
        setImportFailureDialog(null);
        setIsOpen(true);
      }}
    />
    </>
  );
}
